import { Type, Image, Heading1, Mouse, Columns, Menu, Minus, Space, Code, Share2, Video, Table } from 'lucide-react'
import { BLOCK_TYPES, createBlock } from '../utils/blockDefaults'
import { useBuilder } from '../context/BuilderContext'
import { useDraggable } from '@dnd-kit/core'
import SavedBlocks from './SavedBlocks'

const TOOL_CATEGORIES = [
  {
    label: 'Content',
    items: [
      { type: BLOCK_TYPES.TEXT, icon: Type, label: 'Text' },
      { type: BLOCK_TYPES.HEADING, icon: Heading1, label: 'Heading' },
      { type: BLOCK_TYPES.IMAGE, icon: Image, label: 'Image' },
      { type: BLOCK_TYPES.BUTTON, icon: Mouse, label: 'Button' },
      { type: BLOCK_TYPES.VIDEO, icon: Video, label: 'Video' },
    ]
  },
  {
    label: 'Layout',
    items: [
      { type: BLOCK_TYPES.COLUMNS, icon: Columns, label: 'Columns' },
      { type: BLOCK_TYPES.DIVIDER, icon: Minus, label: 'Divider' },
      { type: BLOCK_TYPES.SPACER, icon: Space, label: 'Spacer' },
    ]
  },
  {
    label: 'Advanced',
    items: [
      { type: BLOCK_TYPES.SOCIAL, icon: Share2, label: 'Social' },
      { type: BLOCK_TYPES.MENU, icon: Menu, label: 'Menu' },
      { type: BLOCK_TYPES.TABLE, icon: Table, label: 'Table' },
      { type: BLOCK_TYPES.HTML, icon: Code, label: 'HTML' },
    ]
  }
]

function DraggableToolItem({ type, icon: Icon, label, onAddBlock }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `toolbox-${type}`,
    data: { type, source: 'toolbox' }
  })

  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onAddBlock(type)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.75rem 0.5rem',
        background: isDragging ? 'linear-gradient(135deg, var(--color-primary) 0%, #818cf8 100%)' : '#fafbfc',
        border: isDragging ? '1.5px solid var(--color-primary)' : '1.5px solid #e5e7eb',
        borderRadius: '10px',
        color: isDragging ? '#ffffff' : '#1e293b',
        cursor: isDragging ? 'grabbing' : 'grab',
        fontSize: '0.8rem',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        opacity: isDragging ? 0.7 : 1,
        boxShadow: isDragging ? '0 8px 16px rgba(99, 102, 241, 0.3)' : 'none',
        textAlign: 'center'
      }}
      onMouseOver={(e) => {
        if (!isDragging) {
          e.currentTarget.style.background = '#f1f5f9'
          e.currentTarget.style.borderColor = '#c7d2fe'
          e.currentTarget.style.transform = 'translateY(-1px)'
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.12)'
        }
      }}
      onMouseOut={(e) => {
        if (!isDragging) {
          e.currentTarget.style.background = '#fafbfc'
          e.currentTarget.style.borderColor = '#e5e7eb'
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = 'none'
        }
      }}
    >
      <div style={{
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        background: isDragging ? 'rgba(255,255,255,0.25)' : 'rgba(99, 102, 241, 0.08)',
        color: isDragging ? '#ffffff' : 'var(--color-primary)'
      }}>
        <Icon size={20} style={{ color: isDragging ? '#ffffff' : 'var(--color-primary)' }} />
      </div>
      <span style={{
        fontSize: '0.7rem',
        lineHeight: '1.2',
        color: isDragging ? '#ffffff' : '#475569',
        fontWeight: '500'
      }}>
        {label}
      </span>
    </button>
  )
}

export default function Toolbox({ collapsed }) {
  const { addBlock, bodySettings } = useBuilder()

  const handleAddBlock = (type) => {
    const newBlock = createBlock(type, bodySettings)
    addBlock(newBlock)
  }

  if (collapsed) {
    return null
  }

  return (
    <div style={{
      width: '240px',
      minWidth: '240px',
      backgroundColor: '#ffffff',
      borderRight: '1px solid #e5e7eb',
      padding: '1.25rem 0.875rem',
      overflowY: 'auto',
      transition: 'width 0.2s'
    }}>
      <div style={{
        marginBottom: '1rem',
        paddingBottom: '0.75rem',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h3 style={{
          margin: '0 0 0.2rem 0',
          fontSize: '0.95rem',
          fontWeight: '700',
          color: '#1e293b'
        }}>
          Components
        </h3>
        <p style={{
          margin: 0,
          fontSize: '0.75rem',
          color: '#94a3b8'
        }}>
          Drag or click to add
        </p>
      </div>

      {TOOL_CATEGORIES.map((category) => (
        <div key={category.label} style={{ marginBottom: '1rem' }}>
          <h4 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '0.7rem',
            fontWeight: '700',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {category.label}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            {category.items.map(({ type, icon, label }) => (
              <DraggableToolItem
                key={type}
                type={type}
                icon={icon}
                label={label}
                onAddBlock={handleAddBlock}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Saved Blocks */}
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{
          margin: '0 0 0.5rem 0',
          fontSize: '0.7rem',
          fontWeight: '700',
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Saved Blocks
        </h4>
        <SavedBlocks />
      </div>
    </div>
  )
}
